/**
 * parallax.js — Scroll-Driven Parallax Effects
 *
 * Moves elements with [data-parallax-speed] based on scroll position.
 * Uses Lenis for smooth scroll values when available, falls back to
 * native scrollY with rAF throttling.
 *
 * Modes:
 *   - Simple: offset = scrollY × speed (unbounded)
 *   - Contained: element moves within its parent's bounds
 *     (add [data-parallax-contained], speed 0–1 = intensity)
 *
 * Lenis ownership lives in lenis.js — this module just consumes it.
 * If Lenis was initialised (via [data-smooth-scroll] on body), parallax
 * hooks into it. Otherwise it falls back to native scroll.
 *
 * Respects prefers-reduced-motion — does nothing if enabled.
 */

import { getLenis } from "./lenis.js";

// ---------------------------------------------------------------------------
// Parallax update loop
// ---------------------------------------------------------------------------

function createItems() {
  const els = Array.from(document.querySelectorAll("[data-parallax-speed]"));

  return els.map((el) => {
    const raw = parseFloat(el.getAttribute("data-parallax-speed"));
    const speed = isNaN(raw) ? 0 : raw;
    const contained = el.hasAttribute("data-parallax-contained");
    const container = contained ? el.parentElement : null;

    // Ensure non-reveal parallax elements are visible
    if (!el.hasAttribute("data-reveal")) {
      el.style.opacity = "1";
    }

    el.style.willChange = "transform";

    return { el, speed, contained, container };
  });
}

function updatePositions(items, scrollY) {
  const viewportHeight = window.innerHeight;

  items.forEach(({ el, speed, contained, container }) => {
    if (speed === 0) {
      el.style.transform = "translate3d(0, 0, 0)";
      return;
    }

    // Simple mode: global offset
    if (!contained || !container) {
      const offset = scrollY * speed;
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
      return;
    }

    // Contained mode: map scroll progress within parent bounds
    const rect = container.getBoundingClientRect();
    const containerTopDoc = rect.top + scrollY;
    const containerHeight = rect.height;
    const itemHeight = el.offsetHeight;
    const maxTravel = Math.max(0, containerHeight - itemHeight);

    if (maxTravel === 0) {
      el.style.transform = "translate3d(0, 0, 0)";
      return;
    }

    const start = containerTopDoc - viewportHeight;
    const end = containerTopDoc + containerHeight;
    const span = end - start;

    if (span <= 0) {
      el.style.transform = "translate3d(0, 0, 0)";
      return;
    }

    const progress = Math.min(1, Math.max(0, (scrollY - start) / span));
    const y = maxTravel * speed * progress;

    el.style.transform = `translate3d(0, ${y}px, 0)`;
  });
}

// ---------------------------------------------------------------------------
// Scroll binding
// ---------------------------------------------------------------------------

function getScrollY() {
  return (
    window.scrollY ||
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    0
  );
}

function bindToLenis(lenis, items) {
  lenis.on("scroll", (e) => updatePositions(items, e.scroll));
  updatePositions(items, lenis.scroll || 0);
}

function bindToNativeScroll(items) {
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updatePositions(items, getScrollY());
      ticking = false;
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  updatePositions(items, getScrollY());
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

export function initParallax() {
  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReduced) return;

  const items = createItems();
  if (!items.length) return;

  const lenis = getLenis();

  if (lenis) {
    bindToLenis(lenis, items);
  } else {
    bindToNativeScroll(items);
  }

  window.addEventListener("resize", () => {
    updatePositions(items, lenis ? lenis.scroll || 0 : getScrollY());
  });
}
